import React, { createContext, useContext } from 'react'

import { FinanceFinancialWorkspaceFiltersDocument } from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import {
  SaveFilters,
  SavedFilters,
} from 'modules/finance/components/FinancialWorkspace/types/filter'
import { InsightTableType } from 'modules/finance/components/FinancialWorkspace/types/table'

import { CostCenter } from './types'

interface Props {
  type: InsightTableType
  children: React.ReactNode
}

interface IValue {
  costCenters: CostCenter[]
  saveFilters: SaveFilters
  savedFilters: SavedFilters
  resetSavedFilters: () => void
}

const FilterProviderContext = createContext<IValue | undefined>(undefined)

const emptySavedFilters: SavedFilters = {}

export const FilterProvider: React.FC<Props> = ({ children, type }) => {
  const { data, loading, error } = useQuery(FinanceFinancialWorkspaceFiltersDocument)
  const {
    company: { id: companyId },
    access: { id: accessId },
  } = useCurrentSession()

  const [savedFilters, setSavedFilters] = useLocalStorage<SavedFilters>(
    `${companyId}-${accessId}-fw-${type}-filters`,
    emptySavedFilters
  )

  const saveFilters: SaveFilters = useUnsafeCallback(
    (key, value) => {
      setSavedFilters({ ...(savedFilters || {}), [key]: value })
    },
    [savedFilters, setSavedFilters]
  )

  const resetSavedFilters = useUnsafeCallback(() => {
    setSavedFilters(emptySavedFilters)
  }, [setSavedFilters])

  const costCenters = data?.finance.costCentersConnection.nodes || []

  if (error || loading) {
    return null
  }

  return (
    <FilterProviderContext.Provider
      value={{ costCenters, saveFilters, resetSavedFilters, savedFilters: savedFilters || {} }}
    >
      {children}
    </FilterProviderContext.Provider>
  )
}

export const useFilterProvider = () => {
  const context = useContext(FilterProviderContext)
  if (context === undefined) {
    throw new Error('useFilterProvider must be used within a FilterProvider')
  }
  return context
}
